import React, { useState } from 'react';
import { Box, LoadingSpinner } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { CreateBenefitGroupForm } from '@epi-forms/BenefitGroup';
import {
  BenefitGroupName,
  DialogCreateAnotherGroup,
  LunchSettings,
  NotificationCreateAnotherGroup,
  SelectBenefitsBar
} from '@epi-shared/components';
import { useGetDefaultBenefitGroupsSettingsQuery } from '@epi-api/benefitGroups';
import { MyChoiceBenefit } from '@epi-shared/components/BenefitGroups/SettingsSections/MyChoiceBenefit/MyChoiceBenefit';
import { BenefitType } from '@epi-constants/benefitTypes';
import { IBenefits } from '@epi-models/containers/ManageBenefits';
import {
  IBenefitDefaultSettings,
  ICreateBenefitGroupForm
} from '@epi-models/pages/BenefitGroups';
import { mapBenefitsToSettings } from '@epi-helpers/BenefitGroupHelper';
import { LunchFundsLoadingValues } from '@epi-models/pages/Home';

const createDefaultValuesFromSettings = (
  defaultSettings?: IBenefitDefaultSettings
): ICreateBenefitGroupForm => {
  const benefitSettings = mapBenefitsToSettings(defaultSettings) as Record<
    IBenefits,
    {
      maxAmountPerPeriod: number;
      isMaximumSpendingLimit: boolean;
      fundsLoadingType: LunchFundsLoadingValues;
      recurringTopupRequestEnabled: boolean;
    }
  >;

  return {
    groupName: '',
    benefitSettings
  };
};

export const CreateBenefitGroup = () => {
  const { t } = useTranslation();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const { data: benefitsDefaultSettings, isLoading } =
    useGetDefaultBenefitGroupsSettingsQuery();

  const defaultFormSettings = createDefaultValuesFromSettings(
    benefitsDefaultSettings
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!benefitsDefaultSettings?.contractedBenefits.length) {
    return <div>{t('containers.benefit_groups.no_result')}</div>;
  }

  return (
    <CreateBenefitGroupForm
      defaultSettings={defaultFormSettings}
      toggleModal={setIsOpenModal}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <NotificationCreateAnotherGroup />
        <BenefitGroupName />
        <SelectBenefitsBar
          contractedBenefits={benefitsDefaultSettings.contractedBenefits}
        />
        <LunchSettings defaultSettings={benefitsDefaultSettings} />
        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={3} mt={1}>
          {benefitsDefaultSettings?.contractedBenefits.map(
            ({ benefitType, maxAmount, minAmount }) =>
              benefitType !== BenefitType.Lunch ? (
                <MyChoiceBenefit
                  benefitName={benefitType as IBenefits}
                  maxAmounts={[maxAmount, minAmount]}
                  key={`myChoiceBenefit-${benefitType}`}
                />
              ) : null
          )}
        </Box>
      </Box>
      <DialogCreateAnotherGroup
        openModal={isOpenModal}
        toggleModal={setIsOpenModal}
        defaultSettings={defaultFormSettings}
      />
    </CreateBenefitGroupForm>
  );
};
